import React, { createContext, useContext, useState, useEffect } from 'react';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(null);

  // Load permissions from localStorage on mount
  useEffect(() => {
    const loadPermissions = () => {
      try {
        const userData = localStorage.getItem('userData');
        if (userData) {
          const user = JSON.parse(userData);
          if (user.permissions) {
            setPermissions(user.permissions);
          }
        }
      } catch (error) {
        console.error('Error loading permissions:', error);
      }
    };

    loadPermissions();

    // Listen for profile updates to refresh permissions
    const handleProfileUpdate = (event) => {
      const updatedUserData = event.detail;
      if (updatedUserData && updatedUserData.permissions) {
        setPermissions(updatedUserData.permissions);
      }
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  // Helper function to check if user has read permission for a module
  const hasReadPermission = (module) => {
    if (!permissions || !permissions[module]) {
      return false;
    }
    const hasPermission = permissions[module].read === true;
    return hasPermission;
  };

  // Helper function to check if user has add permission for a module
  const hasAddPermission = (module) => {
    if (!permissions || !permissions[module]) {
      return false;
    }
    return permissions[module].add === true;
  };

  // Helper function to check if user has edit permission for a module
  const hasEditPermission = (module) => {
    if (!permissions || !permissions[module]) {
      return false;
    }
    return permissions[module].edit === true;
  };

  // Helper function to check if user has delete permission for a module
  const hasDeletePermission = (module) => {
    if (!permissions || !permissions[module]) {
      return false;
    }
    return permissions[module].delete === true;
  };

  // Helper function to check if user has any permission for a module
  const hasAnyPermission = (module) => {
    if (!permissions || !permissions[module]) {
      return false;
    }
    const modulePermissions = permissions[module];
    return modulePermissions.read || modulePermissions.add || modulePermissions.edit || modulePermissions.delete;
  };

  // Helper function to check specific permission
  const hasPermission = (module, action) => {
    if (!permissions || !permissions[module]) {
      return false;
    }
    return permissions[module][action] === true;
  };

  const value = {
    permissions,
    hasReadPermission,
    hasAddPermission,
    hasEditPermission,
    hasDeletePermission,
    hasAnyPermission,
    hasPermission
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
